import { useQuery } from '@apollo/react-hooks';
import Head from 'next/head';
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import BotsList from '../../components/bots/BotsList';
import BotsFilters from '../../components/filters/BotsFilters';
import { SearchBar } from '../../components/Form';
import { HeadingOne, Subheading } from '../../components/Text';
import { addApolloState, initializeApollo } from '../../lib/apollo';
import {
  BOTS_FILTERS,
  BOTS_LIST,
  TRENDING_BOTS,
} from '../../lib/graphql/queries';
import {
  WHITE_LABEL_MARKET,
  WHITE_LABEL_TWITTER,
  WHITE_LABEL_URL,
} from '../../lib/white-label';
import { analytics } from '../../lib/analytics';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const metaTitle = `Rent a sneaker bot | ${WHITE_LABEL_MARKET}`;
const metaDescription = `Rent sneaker bots on ${WHITE_LABEL_MARKET}`;

function Bots(props: { username?: string }) {
  const [selectedOperatingSystems, setSelectedOperatingSystems] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [onlyAvailableBots, setOnlyAvailableBots] = useState(true);

  const onChangeTags = (e) => {
    setSelectedTags(e || []);
  };
  const onChangeOperatingSystems = (e) => {
    setSelectedOperatingSystems(e || []);
  };

  const [searchQuery, setSearchQuery] = useState('');
  const [pendingQuery, setPendingQuery] = useState('');
  const [queryTimeout, setQueryTimeout] = useState(0);
  useEffect(() => {
    if (queryTimeout) {
      clearTimeout(queryTimeout);
    }
    setQueryTimeout(
      // @ts-ignore
      setTimeout(() => {
        setSearchQuery(pendingQuery);
      }, 400),
    );
  }, [pendingQuery]);

  useEffect(() => {
    analytics.page('Bots', 'Home');
  }, []);

  const { data, loading, error } = useQuery(BOTS_FILTERS);
  const botsListResult = useQuery(BOTS_LIST, {
    variables: {
      searchQuery: searchQuery,
      operatingSystems: selectedOperatingSystems.map((o) => o.value),
      tags: selectedTags.map((t) => t.value),
      sellerUsername: props.username,
    },
  });

  const filteredBots = useMemo(() => {
    return {
      bots: onlyAvailableBots
        ? botsListResult?.data?.bots.filter((bot) => bot.listingsCount > 0)
        : botsListResult?.data?.bots,
    };
  }, [botsListResult, onlyAvailableBots]);

  const title = props.username
    ? `Bot Rentals from ${props.username} `
    : `Bot Rentals - ${WHITE_LABEL_MARKET}`;

  return (
    <Container>
      <Head>
        <title>{title}</title>
        <meta
          property="og:url"
          content={`${WHITE_LABEL_URL}/bots${
            props.username ? `/seller/${props.username}` : ''
          }`}
        />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta
          property="og:image"
          content={`${process.env.BASE_URL}/_next/image?url=%2Fimages%2F${
            !process.env.WHITE_LABEL_ORG ? 'meta.png' : 'botmart-meta.png'
          }&q=50&w=3840`}
        />
        <meta name="twitter:site" content={WHITE_LABEL_TWITTER} />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <meta
          name="twitter:image"
          content={`${process.env.BASE_URL}/_next/image?url=%2Fimages%2F${
            !process.env.WHITE_LABEL_ORG ? 'meta.png' : 'botmart-meta.png'
          }&w=3840&q=50`}
        />
        <meta name="twitter:card" content="summary_large_image" />
      </Head>
      {!props.username && <HeadingOne>Bots</HeadingOne>}
      {props.username && <HeadingOne>Bots from {props.username}</HeadingOne>}
      <Subheading>Our library of software available to rent.</Subheading>
      <div className="flex flex-col lg:flex-row sm:gap-4">
        <SearchBar
          placeholderOverride="Search by name"
          value={pendingQuery}
          onChangeText={setPendingQuery}
        />
        <BotsFilters
          allTags={data && data.allTags ? data.allTags : []}
          allOperatingSystems={
            data && data.allOperatingSystems ? data.allOperatingSystems : []
          }
          selectedTags={selectedTags}
          selectedOperatingSystems={selectedOperatingSystems}
          onChangeTags={onChangeTags}
          onChangeOperatingSystems={onChangeOperatingSystems}
          onChangeAvailableBots={() => {
            setOnlyAvailableBots(!onlyAvailableBots);
          }}
          onlyAvailableBots={onlyAvailableBots}
        />
      </div>
      <BotsList
        searchQuery={searchQuery}
        data={filteredBots}
        error={botsListResult.error}
        loading={botsListResult.loading}
        username={props.username}
      />
    </Container>
  );
}

export async function getStaticProps(ctx) {
  const apolloClient = initializeApollo();
  await Promise.all([
    apolloClient.query({
      query: BOTS_FILTERS,
    }),
    apolloClient.query({
      query: BOTS_LIST,
      variables: {
        searchQuery: '',
        operatingSystems: [],
        tags: [],
        sellerUsername: undefined,
      },
    }),
    apolloClient.query({
      query: TRENDING_BOTS,
      variables: {
        limit: 3,
      },
    }),
  ]);
  return addApolloState(apolloClient, {
    props: {},
    revalidate: 3600,
  });
}

export default Bots;
